import { useState, useEffect, useRef, useCallback } from "react";
import { showToast } from "../toastService";
import { apiFetch } from "../utils/api";
import ContractorCard from "../components/ContractorCard";
import BottomNavbar from "../components/BottomNavbar";
import Header from "../components/Header";
import "./css/FavoriteContractors.css";
import noWorkImage from "../assets/images/no_work.png";

export default function FavoriteContractors() {
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingContractors, setLoadingContractors] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [lang] = useState(localStorage.getItem("app_language") || "en");

  const USER_BASE_URL = process.env.REACT_APP_USER_BASE_URL;
  const VERSION = process.env.REACT_APP_VERSION;
  const LIMIT = 20;

  // Translations
  const t = {
    en: {
      loading: "Loading",
      noFavoriteContractors: "You have not added any contractors to favorites",
      errorLoading: "Failed to load favorite contractors",
    },
    ar: {
      loading: "جاري التحميل",
      noFavoriteContractors: "لم تقم بإضافة أي مقاول إلى المفضلة",
      errorLoading: "فشل تحميل المقاولين المفضلين",
    },
  }[lang];

  // Fetch favorite contractors
  const fetchFavoriteContractors = useCallback(
    async (page = 1) => {
      try {
        setLoadingContractors(true);
        const url = `${USER_BASE_URL}/${VERSION}/contractor/favorites/?limit=${LIMIT}&page=${page}`;

        const data = await apiFetch(url, "GET", null, {});

        if (data && data.response_code === 200) {
          const contractorsList = data.response || [];

          if (page === 1) {
            setContractors(contractorsList);
          } else {
            setContractors((prev) => [...prev, ...contractorsList]);
          }
          setHasMorePages(contractorsList.length === LIMIT);
          setCurrentPage(page);
        }
      } catch (err) {
        console.error("Error fetching favorite contractors:", err);
        showToast(err.message || t.errorLoading, "error");
      } finally {
        setLoadingContractors(false);
        setLoading(false);
      }
    },
    [USER_BASE_URL, VERSION, t.errorLoading]
  );

  // Initial fetch
  useEffect(() => {
    fetchFavoriteContractors(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle favorite update
  const handleFavoriteUpdate = (contractorId, isFavorite) => {
    // If contractor is unfavorited, remove from list
    if (!isFavorite) {
      setContractors((prev) => prev.filter((contractor) => contractor._id !== contractorId));
    } else {
      // If favorited, update the status
      setContractors((prev) =>
        prev.map((contractor) =>
          contractor._id === contractorId
            ? { ...contractor, is_favorite: isFavorite }
            : contractor
        )
      );
    }
  };

  // Infinite scroll observer ref
  const observerTarget = useRef(null);

  // Handle infinite scroll
  const handleLoadMore = useCallback(() => {
    if (!loadingContractors && hasMorePages) {
      fetchFavoriteContractors(currentPage + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingContractors, hasMorePages, currentPage, fetchFavoriteContractors]);

  // Infinite scroll effect
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMorePages && !loadingContractors) {
          handleLoadMore();
        }
      },
      { threshold: 0.1 }
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [handleLoadMore, hasMorePages, loadingContractors]);

  // Loading screen
  if (loading) {
    return (
      <div className="favorite-contractors-loading">
        <div className="spinner"></div>
        <p>{t.loading}</p>
      </div>
    );
  }

  return (
    <div className="favorite-contractors-container" dir={lang === "ar" ? "rtl" : "ltr"}>
      {/* Header */}
      <Header lang={lang} />

      {/* Main Content */}
      <div className="favorite-contractors-content">
        {loadingContractors && contractors.length === 0 ? (
          // Loading contractors
          <div className="favorite-contractors-loading-top">
            <div className="spinner"></div>
            <p>{t.loading}</p>
          </div>
        ) : contractors.length === 0 ? (
          // No favorite contractors
          <div className="no-contractors-container">
            <img
              src={noWorkImage}
              alt="No Favorite Contractors"
              className="no-contractors-image"
            />
            <p className="no-contractors-message">{t.noFavoriteContractors}</p>
          </div>
        ) : (
          // Contractors list
          <div className="contractors-list">
            {contractors.map((contractor) => (
              <ContractorCard
                key={contractor._id}
                contractor={contractor}
                lang={lang}
                onFavoriteUpdate={handleFavoriteUpdate}
              />
            ))}
            {/* Infinite scroll trigger */}
            {hasMorePages && (
              <div ref={observerTarget} className="infinite-scroll-trigger">
                {loadingContractors && (
                  <div className="loading-more-contractors">
                    <div className="spinner"></div>
                    <span>{t.loading}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom Navigation */}
      <BottomNavbar currentPage="favorites" lang={lang} />
    </div>
  );
}

